import {
  ROOM_DISPLAY_VIDEO_MAX_BYTES,
  ROOM_DISPLAY_VIDEO_MAX_DURATION_SECONDS,
} from "@/modules/room-displays/domain/lib/room-display-media";
import { Button } from "@devhop/ui";
import { Loader2, Upload } from "lucide-react";
import { useCallback, useId, useState } from "react";
import {
  probeVideoDuration,
  uploadRoomDisplayVideoFile,
} from "../lib/room-display-video-upload";

type RoomDisplayVideoUploadProps = {
  screenId: string;
  onSuccess?: (payload: { key: string; videoDurationSec: number }) => void;
  onError?: (error: Error) => void;
  className?: string;
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function RoomDisplayVideoUpload({
  screenId,
  onSuccess,
  onError,
  className,
}: RoomDisplayVideoUploadProps) {
  const inputId = useId();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{
    loaded: number;
    total: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      setUploading(true);
      setProgress({ loaded: 0, total: file.size });
      try {
        const durationSec = await probeVideoDuration(file);
        const key = await uploadRoomDisplayVideoFile(
          screenId,
          file,
          durationSec,
          {
            onProgress: ({ loaded, total }) => setProgress({ loaded, total }),
          },
        );
        setProgress(null);
        onSuccess?.({ key, videoDurationSec: durationSec });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err.message);
        setProgress(null);
        onError?.(err);
      } finally {
        setUploading(false);
      }
    },
    [onError, onSuccess, screenId],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void handleFile(file);
      e.target.value = "";
    },
    [handleFile],
  );

  const progressPercent =
    progress && progress.total > 0
      ? Math.round((progress.loaded / progress.total) * 100)
      : 0;

  return (
    <div className={`space-y-2 ${className ?? ""}`}>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="file"
          accept="video/mp4,video/webm,.mp4,.webm"
          onChange={onInputChange}
          disabled={uploading}
          className="hidden"
          id={inputId}
        />
        <Button
          type="button"
          variant="outline"
          disabled={uploading}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          <span className="ml-2">
            {uploading ? `ກຳລັງອັບໂຫຼດ... ${progressPercent}%` : "ອັບໂຫຼດວິດີໂອ"}
          </span>
        </Button>
      </div>

      {progress && progress.total > 0 ? (
        <div className="space-y-1">
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary transition-[width] duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="text-muted-foreground text-xs">
            {formatBytes(progress.loaded)} / {formatBytes(progress.total)}
          </p>
        </div>
      ) : null}

      <p className="text-muted-foreground text-xs">
        MP4/WebM · ສູງສຸດ {formatBytes(ROOM_DISPLAY_VIDEO_MAX_BYTES)} · ບໍ່ເກີນ{" "}
        {ROOM_DISPLAY_VIDEO_MAX_DURATION_SECONDS}s · chunk ເມື່ອ &gt; 5MB
      </p>
      {error ? <p className="text-destructive text-sm">{error}</p> : null}
    </div>
  );
}
